import http, { endpoints } from '../http';
import {
  mapTaskCategoryToDto,
  mapTaskFiltersFromDto,
  mapTaskOrderByParamToDto,
  mapTaskStatusToDto,
  mapVirtualStagingFromDto,
  mapCallProposalFromDto,
  mapDatahubTaskFromDto,
  mapInterviewQuestionFromDto,
} from './helpers';
import {
  IGetTaskListParams,
  IGetTasksListResponse,
  ITaskInputText,
  OrderVirtualStagingProps,
  IRequestObjectsRemoval,
  ITaskCreateCommentParams,
  IUpdateBookingTypeParams,
  IPhotographerBookingData,
  IBeforeAfterParams,
  IDatahubTask,
  ISomeGifParams,
  VirtualStagingType,
} from './models';
import { IDatahubCase } from '../cases/models';
import {
  ICallProposalDto,
  ICopywriteTextsSubmitParams,
  IDatahubTaskDto,
  IInterviewQuestionDto,
  IAITextQuestionnaireDto,
  ISubmitUploadsParams,
  ITaskFiltersDto,
  IVirtualStagingDto,
} from './dtos';
import { IBookingInfo, IDatahubOrderLine, ISendThirdPartyBooking, ISimplifiedOrder } from '../orders/models';
import { IUser } from '../users/models';
import { ISimplifiedVisualMaterial } from '../material/models';
import { ICommonResponse } from '../types';
import { ICaseTextDto } from '../texts/dtos';
import { groupTextsFromDto } from '../texts/helpers';

const datahubClient = http.createClient(`${endpoints.datahub}`);

export default class TasksApi {
  static async getList({
    query,
    category = [],
    status = [],
    orderBy,
    orderByDirection,
    branchIds = [],
    page = 0,
    pageSize = 20,
    signal,
  }: IGetTaskListParams): Promise<IGetTasksListResponse> {
    const response = await datahubClient.post(
      '/orders/tasks/search',
      {
        skip: page * pageSize,
        take: pageSize,
        branchIds: branchIds?.length !== 0 ? branchIds : undefined,
        searchString: query,
        types: category.length !== 0 ? category?.map(mapTaskCategoryToDto) : undefined,
        statuses: status.length !== 0 ? status?.map(mapTaskStatusToDto) : undefined,
        orderBy: mapTaskOrderByParamToDto(orderBy),
        orderByDirection: orderByDirection,
        // orderByDirection: orderByDirection && orderByDirection === 'descend' ? 'desc' : 'asc',
      },
      {
        signal,
      }
    );

    return {
      page: response?.data?.currentSkip ? Math.ceil(response.data.currentSkip / pageSize) : page,
      pageSize: response?.data?.take || pageSize,
      totalItems: response?.data?.total || 0,
      items: response?.data?.data?.map(mapDatahubTaskFromDto) || [],
    };
  }

  static async getTotalTasksForUser(userId: IUser['id']) {
    const response = await datahubClient.get<{ total: number }>(`/users/${userId}/tasks/total`);
    return response?.data?.total || 0;
  }

  static async getOpenTasksByCaseId(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<IDatahubTaskDto[]>(`/cases/${caseId}/tasks/open`);
    return response?.data?.map(mapDatahubTaskFromDto) || [];
  }

  static async getFilters() {
    const response = await datahubClient.get<ITaskFiltersDto>('/orders/tasks/search/filters');
    return response?.data ? mapTaskFiltersFromDto(response.data) : undefined;
  }

  static async createComment({ taskId, comment, email }: ITaskCreateCommentParams) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/notes/add`, {
      note: comment,
      addedByEmail: email,
    });

    return response?.data;
  }

  static async completeTask(taskId: IDatahubTask['id']) {
    const response = await datahubClient.patch<ICommonResponse>(`/orders/tasks/${taskId}/complete`);
    return response?.data;
  }

  static async sendEmailForInterview({ taskId, ...params }: ITaskInputText) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/interviews/send`, params);
    return response?.data;
  }

  static async getInputForSpeakSlideShowText(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<ICaseTextDto[]>('/orders/tasks/texts/InputForSpeakSlideShow', {
      params: { caseId },
    });
    return response?.data ? groupTextsFromDto(response.data) : [];
  }

  static async getPrefilledText(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<ICaseTextDto[]>(`/orders/tasks/texts/Description`, { params: { caseId } });
    return response?.data ? groupTextsFromDto(response.data) : [];
  }

  static async getCopyWriteText(orderlineId: IDatahubOrderLine['id']) {
    const response = await datahubClient.get<
      {
        sectionName: string;
        content: string;
      }[]
    >(`/orders/tasks/texts/${orderlineId}/TextSections`);
    return response?.data || [];
  }

  static async saveCopyWriteText(params: ICopywriteTextsSubmitParams) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/texts/complete`, params);
    return response?.data;
  }

  static async getVirtualStagingData(type: VirtualStagingType) {
    const response = await datahubClient.get<IVirtualStagingDto[]>('/orders/tasks/virtual-staging', {
      params: { type },
    });

    return response?.data.map(mapVirtualStagingFromDto) || [];
  }

  static async orderVirtualStaging({ taskId, ...params }: OrderVirtualStagingProps) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/tasks/${taskId}/complete/virtual-staging`,
      params
    );
    return response?.data;
  }

  static async requestImageObjectsRemoval(params: IRequestObjectsRemoval) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/tasks/${params.taskId}/complete/objects-removal`,
      params
    );

    return response?.data;
  }

  static async requestRenovation(params: IRequestObjectsRemoval) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/tasks/${params.taskId}/complete/indoor-renovation`,
      params
    );

    return response?.data;
  }

  static async getCallProposal(bookingId: IBookingInfo['bookingId']) {
    const response = await datahubClient.get<ICallProposalDto>(`/Bookings/${bookingId}/proposals`);

    return response?.data?.suggestedProposal ? mapCallProposalFromDto(response.data) : undefined;
  }

  static async confirmBookingPhotographer(proposal: IPhotographerBookingData) {
    const response = await datahubClient.post<ICommonResponse>(`/Bookings/${proposal.bookingId}/proposals/confirm`, {
      proposalId: proposal.id,
    });
    return response?.data;
  }

  static async contactPersonBooking(params: ISendThirdPartyBooking) {
    const response = await datahubClient.post<ICommonResponse>(`/Bookings/third-party-link`, params);

    return response?.data;
  }

  static async updateBookingType(params: IUpdateBookingTypeParams) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/tasks/${params.taskId}/updateType?bookingId=${params.bookingId}`,
      {
        changeToType: params.bookingType,
        responsibleUser: params.responsibleUser,
        person: params.person,
      }
    );
    return response?.data;
  }

  static async partiallyFinishTask(taskId: IDatahubTask['id']) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/partially`);
    return response?.data;
  }

  static async createBeforeAfterSlider({ taskId, ...params }: IBeforeAfterParams) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/complete/beforeafter`, params);

    return response?.data;
  }

  static async completeSoMeGif({ taskId, ...params }: ISomeGifParams) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/complete/some-gif`, params);

    return response?.data;
  }

  static async getSocialMediaTypes() {
    const response = await datahubClient.get<string[]>('/orders/tasks/sometypes');
    return response?.data;
  }

  static async getSocialMediaSlideshowTypes() {
    // TODO: get from API. Mocked for now
    return ['Style1', 'Style2'];
  }

  static async completeSocialMediaTask({ taskId, type }: { taskId: IDatahubTask['id']; type: string }) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/complete/some`, {
      type,
    });
    return response?.data;
  }

  static async completeImageSelection({
    taskId,
    materialIds,
  }: {
    taskId: IDatahubTask['id'];
    materialIds: ISimplifiedVisualMaterial['id'][];
  }) {
    const response = await datahubClient.post<ICommonResponse>(`/orders/tasks/${taskId}/complete/imageselector`, {
      materials: materialIds,
    });
    return response?.data;
  }

  static async completeWhiteboxImageSelection({
    taskId,
    materials,
  }: {
    taskId: IDatahubTask['id'];
    materials: {
      assetId: ISimplifiedVisualMaterial['id'];
      url?: ISimplifiedVisualMaterial['source'];
    }[];
  }) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/tasks/${taskId}/complete/whitebox-imageselector`,
      {
        materials,
      }
    );
    return response?.data;
  }

  static async getOrderTasks(orderId: ISimplifiedOrder['id']) {
    const response = await datahubClient.get<IDatahubTaskDto[]>(`/orders/${orderId}/tasks`);
    return response?.data?.map(mapDatahubTaskFromDto) || [];
  }

  static async getInterviewQuestions(taskId: IDatahubTask['id']) {
    const response = await datahubClient.get<IInterviewQuestionDto[]>(`/orders/tasks/${taskId}/interviews/questions`);

    return response?.data ? response.data.map(mapInterviewQuestionFromDto) : undefined;
  }

  static async getAITextQuestionnaire(taskId: IDatahubTask['id']) {
    const response = await datahubClient.get<IAITextQuestionnaireDto>(`/orders/tasks/${taskId}/questionnaire`);

    return response?.data
      ? {
          questions: (response.data.questions ?? []).map(mapInterviewQuestionFromDto),
          isSendEmailAvailable: response.data.isSendEmailAvailable ?? false,
        }
      : undefined;
  }

  static async getAITextEditorLink(taskId: IDatahubTask['id']) {
    const response = await datahubClient.get<string>(`/orders/tasks/${taskId}/aitexteditorlink`);
    return response?.data;
  }

  static async saveInterviewQuestions({
    taskId,
    answers,
    isQuestionnaire = false,
  }: {
    taskId: IDatahubTask['id'];
    answers: {
      question: string;
      answer: string;
    }[];
    isQuestionnaire?: boolean;
  }) {
    const response = await datahubClient.post(
      isQuestionnaire ? `/orders/tasks/${taskId}/questionnaire` : `/orders/tasks/${taskId}/interviews/complete`,
      answers
    );

    return response?.data;
  }

  static async submitUploads(params: ISubmitUploadsParams) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/tasks/${params.taskId}/uploads`,
      params.uploads
    );

    return response?.data;
  }

  static async getUploads(taskId: IDatahubTask['id']) {
    const response = await datahubClient.get<ISubmitUploadsParams['uploads']>(`/orders/tasks/${taskId}/uploads`);

    return response?.data || [];
  }

  static async getBookingTaskByBookingId(bookingId: string) {
    const response = await datahubClient.get<string>(`/Bookings/${bookingId}/task`);

    return response?.data;
  }
}
